import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

export interface ChangelogSectionOverviewDto {
	sectionName: string;
	itemCount: number;
}

export interface ChangelogItemDto {
	sortOrder: number;
	description?: string | null;
	sourceFile?: string | null;
}

export interface ChangelogSectionDto {
	sectionName: string;
	itemCount: number;
	items: ChangelogItemDto[];
}

export interface ChangelogFragmentDto {
	relativePath?: string | null;
	fileName?: string | null;
	markdown?: string | null;
}

export interface ChangelogDetailDto {
	id: number;
	applicationKey: string;
	version: string;
	applicationName?: string | null;
	schemaVersion?: number | null;
	releaseDate?: string | null;
	exportedAtUtc?: string | null;
	createdAtUtc: string;
	updatedAtUtc: string;
	isPreRelease: boolean;
	sourceBranch?: string | null;
	buildVersion?: string | null;
	payloadParsed: boolean;
	payloadParseError?: string | null;
	sections: ChangelogSectionDto[];
	fragments: ChangelogFragmentDto[];
	rawPayloadJson: string;
}

export interface ChangelogValidationWarningDto {
	code: string;
	message: string;
}

export interface ChangelogReleaseOverviewDto {
	id?: number | null;
	releaseFamilyKey: string;
	releaseFamilyName: string;
	version: string;
	releaseDate?: string | null;
	exportedAtUtc?: string | null;
	latestSourceCreatedAtUtc: string;
	latestSourceUpdatedAtUtc: string;
	isPreRelease: boolean;
	status: string;
	exists: boolean;
	publishedAtUtc?: string | null;
	sourceCount: number;
	itemCount: number;
	visibleForUsersItemCount: number;
	sourceApplicationKeys: string[];
	highlights: string[];
}

export interface ChangelogReleaseSourceDto {
	id: number;
	applicationKey: string;
	applicationName?: string | null;
	version: string;
	releaseDate?: string | null;
	exportedAtUtc?: string | null;
	createdAtUtc: string;
	updatedAtUtc: string;
	isPreRelease: boolean;
	sourceBranch?: string | null;
	buildVersion?: string | null;
	payloadParsed: boolean;
	payloadParseError?: string | null;
	itemCount: number;
	sections: ChangelogSectionOverviewDto[];
	highlights: string[];
}

export interface ChangelogTargetApplicationOptionDto {
	applicationKey: string;
	displayName: string;
}

export interface GroupedChangelogSourceReferenceDto {
	sourceChangelogId: number;
	sourceApplicationKey: string;
	sourceApplicationName?: string | null;
	rawSortOrder: number;
	rawSectionName: string;
	sourceFile?: string | null;
}

export interface GroupedChangelogItemDto {
	id: string;
	text: string;
	visibility: string;
	scope: string;
	targetApplicationKeys: string[];
	isManuallyEdited: boolean;
	sourceReferences: GroupedChangelogSourceReferenceDto[];
}

export interface GroupedChangelogSectionDto {
	sectionName: string;
	items: GroupedChangelogItemDto[];
}

export interface GroupedChangelogPayloadDto {
	releaseFamilyKey: string;
	releaseFamilyName: string;
	version: string;
	releaseDate?: string | null;
	title: string;
	summary?: string | null;
	sourceApplicationKeys: string[];
	sections: GroupedChangelogSectionDto[];
}

export interface ChangelogReleaseEditorDto {
	id?: number | null;
	releaseFamilyKey: string;
	releaseFamilyName: string;
	version: string;
	isPreRelease: boolean;
	status: string;
	exists: boolean;
	lastGeneratedAtUtc?: string | null;
	publishedAtUtc?: string | null;
	createdAtUtc?: string | null;
	updatedAtUtc?: string | null;
	createdByUserId?: number | null;
	updatedByUserId?: number | null;
	allowedStatuses: string[];
	allowedSections: string[];
	allowedVisibilityTargets: string[];
	allowedScopes: string[];
	allowedPreviewAudiences: string[];
	availableApplications: ChangelogTargetApplicationOptionDto[];
	sources: ChangelogReleaseSourceDto[];
	availableSources: ChangelogReleaseSourceDto[];
	suggestedPayload: GroupedChangelogPayloadDto;
	draftPayload: GroupedChangelogPayloadDto;
	publishedPayload?: GroupedChangelogPayloadDto | null;
	validationWarnings: ChangelogValidationWarningDto[];
	draftPayloadParseError?: string | null;
	publishedPayloadParseError?: string | null;
}

export interface ChangelogReleasePreviewDto {
	id?: number | null;
	releaseFamilyKey: string;
	releaseFamilyName: string;
	version: string;
	isPreRelease: boolean;
	status: string;
	audience: string;
	stage: string;
	applicationKeys: string[];
	payload: GroupedChangelogPayloadDto;
	validationWarnings: ChangelogValidationWarningDto[];
}

export interface SaveChangelogReleaseDto {
	status?: string | null;
	draftPayload: GroupedChangelogPayloadDto;
	publishedPayload?: GroupedChangelogPayloadDto | null;
	sourceChangelogIds?: number[] | null;
}

export interface BulkUpdateChangelogReleaseDto {
	itemIds: string[];
	sectionName?: string | null;
	visibility?: string | null;
	scope?: string | null;
	targetApplicationKeys?: string[] | null;
}

export interface ChangelogReleaseOverviewQuery {
	releaseFamilyKey?: string | null;
	includePreRelease?: boolean;
	take?: number;
}

export interface ChangelogReleasePreviewQuery {
	audience?: string | null;
	stage?: string | null;
	applicationKeys?: string[] | null;
}

@Injectable({
	providedIn: 'root',
})
export class ChangelogService {
	private readonly rawUrl = 'changelog';
	private readonly releaseUrl = 'changelogrelease-linking';
	private readonly adminReleaseUrl = 'changelogrelease-linking/admin/releases';

	constructor(private http: HttpClient) {}

	public getSourceById(id: number): Observable<ChangelogDetailDto> {
		return this.http.get<ChangelogDetailDto>(`${this.rawUrl}/${id}`);
	}

	public getReleaseOverview(query: ChangelogReleaseOverviewQuery = {}): Observable<ChangelogReleaseOverviewDto[]> {
		let params = new HttpParams();

		if (query.releaseFamilyKey) {
			params = params.set('releaseFamilyKey', query.releaseFamilyKey);
		}

		if (query.includePreRelease != null) {
			params = params.set('includePreRelease', String(query.includePreRelease));
		}

		if (query.take != null) {
			params = params.set('take', String(query.take));
		}

		return this.http.get<ChangelogReleaseOverviewDto[]>(this.adminReleaseUrl, { params });
	}

	public getRelease(releaseFamilyKey: string, version: string, isPreRelease = false): Observable<ChangelogReleaseEditorDto> {
		const params = new HttpParams().set('isPreRelease', String(isPreRelease));
		return this.http.get<ChangelogReleaseEditorDto>(
			`${this.adminReleaseUrl}/${encodeURIComponent(releaseFamilyKey)}/${encodeURIComponent(version)}`,
			{ params },
		);
	}

	public getReleasePreview(
		releaseFamilyKey: string,
		version: string,
		isPreRelease = false,
		query: ChangelogReleasePreviewQuery = {},
	): Observable<ChangelogReleasePreviewDto> {
		let params = new HttpParams().set('isPreRelease', String(isPreRelease));

		if (query.audience) {
			params = params.set('audience', query.audience);
		}

		if (query.stage) {
			params = params.set('stage', query.stage);
		}

		for (const applicationKey of query.applicationKeys ?? []) {
			params = params.append('applicationKeys', applicationKey);
		}

		return this.http.get<ChangelogReleasePreviewDto>(
			`${this.adminReleaseUrl}/${encodeURIComponent(releaseFamilyKey)}/${encodeURIComponent(version)}/preview`,
			{ params },
		);
	}

	public saveRelease(
		releaseFamilyKey: string,
		version: string,
		isPreRelease: boolean,
		request: SaveChangelogReleaseDto,
	): Observable<ChangelogReleaseEditorDto> {
		const params = new HttpParams().set('isPreRelease', String(isPreRelease));
		return this.http.put<ChangelogReleaseEditorDto>(
			`${this.adminReleaseUrl}/${encodeURIComponent(releaseFamilyKey)}/${encodeURIComponent(version)}`,
			request,
			{ params },
		);
	}

	public bulkUpdateRelease(
		releaseFamilyKey: string,
		version: string,
		isPreRelease: boolean,
		request: BulkUpdateChangelogReleaseDto,
	): Observable<ChangelogReleaseEditorDto> {
		const params = new HttpParams().set('isPreRelease', String(isPreRelease));
		return this.http.post<ChangelogReleaseEditorDto>(
			`${this.adminReleaseUrl}/${encodeURIComponent(releaseFamilyKey)}/${encodeURIComponent(version)}/bulk-update`,
			request,
			{ params },
		);
	}

	public getPublishedReleases(take = 20): Observable<unknown> {
		const params = new HttpParams().set('take', String(take));
		return this.http.get(`${this.releaseUrl}/published`, { params });
	}
}
